import { FaTimes, FaDownload } from "react-icons/fa";
import Pagination from "Components/Pagination";
import { designTokens } from "utils/designSystem";

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "24px",
  },
  container: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: designTokens.shadows.lg,
    width: "100%",
    maxWidth: "860px",
    height: "680px",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px",
    borderBottom: "1px solid #e5e7eb",
  },
  title: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1f2937",
    margin: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  csvBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#ffffff",
    backgroundColor: designTokens.colors.success,
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  csvBtnDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  closeBtn: {
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: "50%",
    backgroundColor: "transparent",
    color: "#9ca3af",
    cursor: "pointer",
  },
  summary: {
    display: "flex",
    gap: "20px",
    padding: "14px 24px",
    fontSize: "14px",
    color: "#6b7280",
    borderBottom: "1px solid #f3f4f6",
    flexWrap: "wrap",
  },
  summaryStrong: {
    color: "#1f2937",
    fontWeight: "600",
  },
  body: {
    flex: 1,
    overflowY: "auto",
    padding: "0 24px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  },
  th: {
    textAlign: "left",
    padding: "12px 10px",
    color: "#6b7280",
    fontWeight: "600",
    borderBottom: "1px solid #e5e7eb",
    position: "sticky",
    top: 0,
    backgroundColor: "#ffffff",
  },
  td: {
    padding: "12px 10px",
    borderBottom: "1px solid #f3f4f6",
    color: "#1f2937",
  },
  loading: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
    fontSize: "14px",
  },
  empty: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
    fontSize: "14px",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 24px",
    borderTop: "1px solid #e5e7eb",
  },
  pageInfo: {
    fontSize: "13px",
    color: "#6b7280",
  },
};

const UtmDetailModal = ({
  target,
  periodLabel,
  detail,
  isLoading,
  page = 1,
  limit = 10,
  onPageChange,
  onDownloadCsv,
  downloading,
  onClose,
}) => {
  const total = detail?.total ?? target?.signups ?? 0;
  const items = detail?.items || [];
  const totalPages = Math.max(1, Math.ceil(total / limit));

  // 공용 Pagination 은 setPageInfo 형태를 기대하므로 onPageChange 로 변환
  const handlePageInfoChange = (updater) => {
    if (isLoading) return;
    const next =
      typeof updater === "function"
        ? updater({ page, totalPage: totalPages })
        : updater;
    if (next?.page && next.page !== page) onPageChange(next.page);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.container} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            {target.source} · {target.medium || "-"} · {target.campaign || "(캠페인 없음)"}
          </h2>
          <div style={styles.headerActions}>
            <button
              style={{
                ...styles.csvBtn,
                ...(downloading || total === 0 ? styles.csvBtnDisabled : {}),
              }}
              onClick={onDownloadCsv}
              disabled={downloading || total === 0}
            >
              <FaDownload size={12} />
              {downloading ? "다운로드 중..." : "CSV 다운로드"}
            </button>
            <button style={styles.closeBtn} onClick={onClose}>
              <FaTimes size={16} />
            </button>
          </div>
        </div>

        <div style={styles.summary}>
          <span>{periodLabel}</span>
          <span>
            가입 <span style={styles.summaryStrong}>{total.toLocaleString()}명</span>
          </span>
          {target.visits != null && (
            <span>
              방문 <span style={styles.summaryStrong}>{target.visits.toLocaleString()}회</span>
            </span>
          )}
        </div>

        {isLoading ? (
          <div style={styles.loading}>로딩 중...</div>
        ) : items.length === 0 ? (
          <div style={styles.empty}>가입자가 없습니다.</div>
        ) : (
          <div style={styles.body}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>이름</th>
                  <th style={styles.th}>아이디</th>
                  <th style={styles.th}>이메일</th>
                  <th style={styles.th}>가입일</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={`${item.user_id}-${idx}`}>
                    <td style={styles.td}>{item.user_name}</td>
                    <td style={styles.td}>{item.user_id}</td>
                    <td style={styles.td}>{item.email}</td>
                    <td style={styles.td}>{item.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > 0 && (
          <div style={styles.footer}>
            <span style={styles.pageInfo}>
              {page} / {totalPages} 페이지 · 전체 {total.toLocaleString()}명
            </span>
            <Pagination
              pageInfo={{ page, totalPage: totalPages }}
              setPageInfo={handlePageInfoChange}
              syncQuery={false}
              compact
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default UtmDetailModal;
